import { new__cn as cn, createTwStyle } from '@/component/UI/util';

import { BUTTON_SIZE, BUTTON_VARIANT } from './constant';
import type { VButtonProps } from './type';

export const TW_BUTTON_VARIANT = {
  [BUTTON_VARIANT.PRIMARY]: cn(
    'text-white',
    'bg-primary-500',
    'enabled:hover:bg-primary-400',
    'enabled:active:bg-primary-600',

    'data-[outlined=true]:text-primary-500',
    'data-[outlined=true]:outline-border-primary-500',
    'data-[outlined=true]:hover:outline-border-primary-400',
    'data-[outlined=true]:active:outline-border-primary-600',
  ),

  [BUTTON_VARIANT.SECONDARY]: cn(
    'text-white',
    'bg-secondary-500',
    'enabled:hover:bg-secondary-400',
    'enabled:active:bg-secondary-600',

    'data-[outlined=true]:text-secondary-500',
    'data-[outlined=true]:outline-border-secondary-500',
    'data-[outlined=true]:hover:outline-border-secondary-400',
    'data-[outlined=true]:active:outline-border-secondary-600',
  ),

  [BUTTON_VARIANT.DANGER]: cn(
    'text-white',
    'bg-error-500',
    'enabled:hover:bg-error-400',
    'enabled:active:bg-error-600',

    'data-[outlined=true]:text-error-500',
    'data-[outlined=true]:outline-border-error-500',
    'data-[outlined=true]:hover:outline-border-error-400',
    'data-[outlined=true]:active:outline-border-error-600',
  ),

  [BUTTON_VARIANT.CANCEL]: cn(
    'text-white',
    'bg-gray-500',
    'enabled:hover:bg-gray-400',
    'enabled:active:bg-gray-600',

    'data-[outlined=true]:text-gray-500',
    'data-[outlined=true]:outline-border-gray-500',
    'data-[outlined=true]:hover:outline-border-gray-400',
    'data-[outlined=true]:active:outline-border-gray-600',
  ),

  /** @deprecated */
  [BUTTON_VARIANT.DANGER_OUTLINE]: cn(
    'text-error-500',
    'bg-white',
    'outline-border-error-500',
    'enabled:hover:outline-border-error-400',
    'enabled:active:outline-border-error-600',

    'disabled:outline-border-gray-300',
    'aria-busy:outline-border-gray-300',
  ),

  [BUTTON_VARIANT.TRANSPARENT]: 'bg-none border border-transparent',
} as const;

export const TW_BUTTON_SIZE = {
  [BUTTON_SIZE.XS]: cn(
    'px-scale/4 py-scale/2',
    'h-5 rounded-sm',
    'text-body-sm font-semibold',
    '[&_svg]:size-3',
  ),
  [BUTTON_SIZE.SM]: cn(
    'px-scale/8 py-scale/4',
    'h-6.5 rounded-sm',
    'text-body-md font-semibold',
    '[&_svg]:size-4',
  ),
  [BUTTON_SIZE.MD]: cn(
    'px-scale/16 py-scale/4',
    'h-7.5 rounded-md',
    'text-body-lg font-semibold',
    '[&_svg]:size-4',
  ),
  [BUTTON_SIZE.LG]: cn(
    'px-scale/24 py-scale/8',
    'h-10 rounded-md',
    'text-body-xl font-bold',
    '[&_svg]:size-5',
  ),
} as const;

export const TW_OUTLINED_BUTTON = [
  cn(
    'data-[outlined=true]:bg-transparent',
    'data-[outlined=true]:enabled:hover:bg-transparent',
    'data-[outlined=true]:enabled:active:bg-transparent',

    'data-[outlined=true]:disabled:bg-gray-200',
    'data-[outlined=true]:disabled:outline-border-gray-300',
    'data-[outlined=true]:disabled:text-gray-300',

    'data-[outlined=true]:aria-busy:bg-gray-200',
    'data-[outlined=true]:aria-busy:text-status-inProgress',
    'data-[outlined=true]:aria-busy:hover:bg-gray-200',
    'data-[outlined=true]:aria-busy:hover:text-status-inProgress',
    'data-[outlined=true]:aria-busy:hover:shadow-none',
    'data-[outlined=true]:aria-busy:outline-border-gray-300',
  ),
];

export const TW_BUTTON_BASE = [
  cn(
    'flex items-center justify-center gap-1 enabled:hover:shadow-sm enabled:active:shadow-none',
  ),
  cn('disabled:bg-gray-200 disabled:text-gray-300'),
  cn(
    'aria-busy:bg-gray-200 aria-busy:text-status-inProgress',
    'aria-busy:hover:bg-gray-200 aria-busy:hover:text-status-inProgress aria-busy:hover:shadow-none',
    'aria-busy:cursor-wait!',
  ),
];

const twStyle =
  createTwStyle<
    Pick<VButtonProps, 'fluid' | 'outlined' | 'size' | 'variant'>
  >();

export default twStyle({
  button: ({
    fluid,
    outlined = false,
    size = BUTTON_SIZE.MD,
    variant = BUTTON_VARIANT.PRIMARY,
  }) => {
    return cn(
      TW_BUTTON_BASE,
      TW_BUTTON_VARIANT[variant],
      TW_BUTTON_SIZE[size],
      outlined ? TW_OUTLINED_BUTTON : '',
      { 'w-full': fluid },
    );
  },
});
